import React, { useContext, useState } from 'react';
import { Box, Button, Dialog, Typography, useTheme } from '@mui/material';
import { ReactComponent as DashboardIcon } from '../assets/icons/base/home.svg';
import { ReactComponent as RealTimeViewIcon } from '../assets/icons/base/radio.svg';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ColorModeContext } from '../theme';
import { RootState } from '../redux/store';
import { logoutUser } from '../redux/slices/userProfile';
import { adminPermission, rtvPermission, searchPermission } from '../constants/userPermissions';

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const dispatch = useDispatch();

  const [confirmLogout, setConfirmLogout] = useState(false);

  // Mirror the desktop Sidebar's permission filtering — a tab whose route
  // isn't registered would just bounce back to the default route and look
  // broken.
  const permissions = useSelector(
    (store: RootState) => store.user.user?.permissions?.map((perm) => perm.action) ?? [],
  );
  const isAdmin = permissions.includes(adminPermission);
  const canSearch = isAdmin || permissions.includes(searchPermission);
  const canRtv = isAdmin || permissions.includes(rtvPermission);

  const isSearch = location.pathname === '/dashboard';
  const isRTV = location.pathname === '/real-time-view';

  const handleLogout = () => {
    setConfirmLogout(false);
    dispatch(logoutUser());
    navigate('/');
  };

  return (
    <Box
      display="flex"
      borderTop="1px solid"
      borderColor="divider"
      bgcolor="background.default"
      sx={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 20px)' }}
    >
      {canSearch && (
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          alignItems="center"
          py={1}
          onClick={() => navigate('/dashboard')}
          sx={{
            cursor: 'pointer',
            color: isSearch
              ? 'primary.main'
              : 'text.disabled',
          }}
        >
          <DashboardIcon style={{ width: 20, height: 20, stroke: 'currentColor' }} />
          <Typography
            fontSize="10px"
            fontWeight={isSearch ? 600 : 400}
          >
            Dashboard
          </Typography>
        </Box>
      )}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        alignItems="center"
        py={1}
        onClick={() => {
          colorMode.toggleColorMode();
          localStorage.setItem(
            'colorMode',
            theme.palette.mode === 'light' ? 'dark' : 'light',
          );
        }}
        sx={{
          cursor: 'pointer',
          color: 'text.disabled',
        }}
      >
        {theme.palette.mode === 'dark'
          ? <LightModeIcon fontSize="small" />
          : <DarkModeIcon fontSize="small" />}
        <Typography
          fontSize="10px"
          fontWeight={400}
        >
          {theme.palette.mode === 'dark'
            ? 'Light'
            : 'Dark'}
        </Typography>
      </Box>
      {canRtv && (
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          alignItems="center"
          py={1}
          onClick={() => navigate('/real-time-view')}
          sx={{
            cursor: 'pointer',
            color: isRTV
              ? 'error.main'
              : 'text.disabled',
          }}
        >
          <RealTimeViewIcon style={{ width: 20, height: 20, stroke: 'currentColor' }} />
          <Typography
            fontSize="10px"
            fontWeight={isRTV ? 600 : 400}
          >
            RTV
          </Typography>
        </Box>
      )}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        alignItems="center"
        py={1}
        onClick={() => setConfirmLogout(true)}
        sx={{
          cursor: 'pointer',
          color: 'text.disabled',
        }}
      >
        <LogoutIcon fontSize="small" />
        <Typography
          fontSize="10px"
          fontWeight={400}
        >
          Log out
        </Typography>
      </Box>

      <Dialog
        open={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        PaperProps={{ sx: { borderRadius: '12px', mx: 3 } }}
      >
        <Box p={3}>
          <Typography
            fontWeight={600}
            mb={2}
          >
            Log out of VIPR?
          </Typography>
          <Box
            display="flex"
            gap={1}
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              onClick={() => setConfirmLogout(false)}
              sx={{ textTransform: 'none', borderRadius: '10px' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleLogout}
              sx={{ textTransform: 'none', borderRadius: '10px' }}
            >
              Log out
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default MobileBottomNav;
